import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CloseIcon from "@mui/icons-material/Close";
import Iconify from "components/Iconify";

const summaryRow = (label, value) => (
  <Box mb={1} sx={{ pb: 1, borderBottom: `1px dashed #dee2e6` }}>
    <Typography
      variant="overline"
      fontSize={10}
      display="block"
      mb={0.5}
      sx={{ color: "#7b809a", letterSpacing: "0.13em" }}
    >
      {label}
    </Typography>
    {value}
  </Box>
);

export const BookingOverview = ({
  values,
  isMember,
  availableAmenities,
  availableAddons,
  setGrandTotal,
  grandTotal,
  activeStep,
  appliedDiscount,
  setAppliedDiscount,
  loading,
  canStep1,
  setSubTotal,
}) => {
  const [discountInput, setDiscountInput] = useState("");
  const [discountError, setDiscountError] = useState("");
  const isMemberType = values?.memberType === "member";

  const findAmenity = (date, id) =>
    availableAmenities?.find((d) => d.date === date)?.amenities?.find((a) => a?.id === id);
  const findAddOn = (id) => availableAddons?.find((a) => a?.id === id);

  const priceOf = (item) =>
    item ? (isMemberType ? item?.member_price : item?.non_member_price) || 0 : 0;

  // ── Build a per-date breakdown from selectedAmenities / selectedAddOns ──
  const dateKeys = Array.from(
    new Set([
      ...Object.keys(values?.selectedAmenities || {}),
      ...Object.keys(values?.selectedAddOns || {}),
    ])
  ).sort((a, b) => dayjs(a).diff(dayjs(b)));

  let totalAmenityPrice = 0;
  let totalAddOnsPrice = 0;

  const dateBreakdown = dateKeys
    .map((date) => {
      const amenities = (values?.selectedAmenities?.[date] || [])
        .map((id) => findAmenity(date, id))
        .filter(Boolean)
        .map((a) => {
          const price = priceOf(a);
          totalAmenityPrice += price;
          return { ...a, price };
        });

      const addons = (values?.selectedAddOns?.[date] || [])
        .map((id) => findAddOn(id))
        .filter(Boolean)
        .map((a) => {
          const price = priceOf(a);
          totalAddOnsPrice += price;
          return { ...a, price };
        });

      const dateSubtotal = [...amenities, ...addons].reduce((s, i) => s + (i.price || 0), 0);

      return { date, amenities, addons, dateSubtotal };
    })
    .filter((d) => d.amenities.length > 0 || d.addons.length > 0);

  const subTotal = totalAmenityPrice + totalAddOnsPrice;
  const finalPrice = Math.max(0, subTotal - appliedDiscount);

  useEffect(() => {
    setSubTotal(subTotal);
  }, [subTotal]);

  useEffect(() => {
    setGrandTotal(finalPrice);
  }, [finalPrice]);

  const handleApplyDiscount = (e) => {
    e.preventDefault();
    const val = parseFloat(discountInput);
    if (isNaN(val) || val < 0) {
      setDiscountError("Enter a valid discount amount.");
      return;
    }
    if (val > subTotal) {
      setDiscountError("Discount cannot exceed the subtotal.");
      return;
    }
    setAppliedDiscount(val);
    setDiscountError("");
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(0);
    setDiscountInput("");
    setDiscountError("");
  };

  useEffect(() => {
    if (appliedDiscount > 0) {
      handleRemoveDiscount();
    }
  }, [values?.selectedAddOns, values?.selectedAmenities]);

  const priceLabel = (price) =>
    price != null && price > 0 ? `₹${price?.toLocaleString("en-IN")}` : "₹0";

  return (
    <Grid item xs={12} lg={4}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid #dee2e6`,
          position: { lg: "sticky" },
          top: { lg: 24 },
        }}
      >
        {/* ── Header ── */}
        <Box sx={{ backgroundColor: "#A68181", p: 3, position: "relative", overflow: "hidden" }}>
          <Typography
            variant="overline"
            sx={{ color: "#ffffff", fontSize: 10, letterSpacing: "0.18em" }}
          >
            Summary
          </Typography>
          <Typography variant="h5" sx={{ color: "#ffffff", fontWeight: 700, mt: 0.25 }}>
            Booking Overview
          </Typography>
        </Box>

        <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>
          {/* ── Date Range ── */}
          {summaryRow(
            "Date",
            values?.start_date ? (
              <Typography fontWeight={700} color="#344767" sx={{ fontSize: 14 }}>
                {dayjs(values?.start_date).format("DD MMM YYYY")} to{" "}
                {dayjs(values?.end_date).format("DD MMM YYYY")}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: "#7b809a", fontStyle: "italic" }}>
                Not selected
              </Typography>
            )
          )}

          {/* ── Slot ── */}
          {summaryRow(
            "Slot",
            values?.slot ? (
              <Typography fontWeight={600} color="#344767" sx={{ fontSize: 14 }}>
                {values?.slot === 1
                  ? "Morning ( 8:00 AM - Next Day 7:00 AM )"
                  : "Evening (4:00 PM - Next Day 3:00 PM)"}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: "#7b809a", fontStyle: "italic" }}>
                Not selected
              </Typography>
            )
          )}

          {/* ── Member Type ── */}
          {summaryRow(
            "Booking For",
            <Chip
              label={isMember ? "Member" : "Non-Member"}
              size="small"
              sx={{
                fontWeight: 700,
                mt: 0.25,
                backgroundColor: isMember ? "#A6818118" : "#f0f2f5",
                color: isMember ? "#9A3B3B" : "#7b809a",
                border: `1px solid ${isMember ? "#A6818144" : "#dee2e6"}`,
              }}
            />
          )}

          {/* ── Occasion ── */}
          {values?.occasion &&
            summaryRow(
              "Occasion",
              <Typography fontWeight={600} color="#344767" sx={{ fontSize: 14 }}>
                {values?.occasion}
              </Typography>
            )}

          {/* ── Date-wise selections ── */}
          {dateBreakdown.length > 0 && (
            <Box mb={1.5}>
              <Typography
                variant="overline"
                fontSize={10}
                display="block"
                mb={1}
                sx={{ color: "#7b809a", letterSpacing: "0.13em" }}
              >
                Selections by Date
              </Typography>

              {dateBreakdown.map(({ date, amenities, addons, dateSubtotal }, idx) => (
                <Box
                  key={date}
                  sx={{
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: idx !== dateBreakdown.length - 1 ? `1px dashed #dee2e6` : "none",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                    <Iconify
                      icon="mdi:calendar-month-outline"
                      sx={{ fontSize: 14, color: "#A68181" }}
                    />
                    <Typography fontWeight={700} fontSize={12.5} color="#344767">
                      {dayjs(date).format("DD MMM YYYY, ddd")}
                    </Typography>
                  </Box>

                  <Stack spacing={0.5} pl={0.5}>
                    {amenities.map((a) => (
                      <Box
                        key={`a-${a.id}`}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" sx={{ fontSize: 12.5, color: "#344767" }}>
                          {a?.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: "#4CAF50", fontSize: 12.5 }}
                        >
                          {priceLabel(a.price)}
                        </Typography>
                      </Box>
                    ))}
                    {addons.map((a) => (
                      <Box
                        key={`ad-${a.id}`}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" sx={{ fontSize: 12.5, color: "#7b809a" }}>
                          + {a?.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: "#4CAF50", fontSize: 12.5 }}
                        >
                          {priceLabel(a.price)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" sx={{ color: "#7b809a", fontSize: 11 }}>
                      Date subtotal
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ color: "#344767", fontSize: 11 }}
                    >
                      {priceLabel(dateSubtotal)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {/* ── Discount Input ── */}
          {subTotal > 0 && (
            <Box mb={2.5} sx={{ pb: 2.5, borderBottom: `1px dashed #dee2e6` }}>
              <Typography
                variant="overline"
                fontSize={10}
                display="block"
                mb={1}
                sx={{ color: "#7b809a", letterSpacing: "0.13em" }}
              >
                Discount
              </Typography>

              {appliedDiscount > 0 ? (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    backgroundColor: "#e8f5e9",
                    border: "1px solid #c8e6c9",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <LocalOfferIcon sx={{ fontSize: 15, color: "#4CAF50" }} />
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: "#2e7d32", fontSize: 13 }}
                    >
                      −₹{appliedDiscount?.toLocaleString("en-IN")} applied
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={handleRemoveDiscount} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 15, color: "#7b809a" }} />
                  </IconButton>
                </Box>
              ) : (
                <Box display="flex" gap={1} alignItems="flex-center">
                  <TextField
                    size="small"
                    placeholder="Enter amount"
                    value={discountInput}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow only whole numbers
                      if (/^\d*$/.test(value)) {
                        setDiscountInput(value);
                        setDiscountError("");
                      }
                    }}
                    error={!!discountError}
                    helperText={discountError}
                    type="text"
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography sx={{ color: "#7b809a", fontSize: 13 }}>₹</Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        fontSize: 13,
                      },
                    }}
                  />
                  <Box
                    component="button"
                    onClick={(e) => handleApplyDiscount(e)}
                    sx={{
                      maxHeight: 40,
                      px: 2,
                      py: "7px",
                      borderRadius: 1.5,
                      border: "1px solid #A68181",
                      backgroundColor: "transparent",
                      color: "#A68181",
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      transition: "all 0.2s",
                      "&:hover": { backgroundColor: "#A68181", color: "#fff" },
                    }}
                  >
                    Apply
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ── Price Breakdown ── */}
          {subTotal > 0 && (
            <Box mb={2} sx={{ px: 1 }}>
              <Stack spacing={0.75}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: "#7b809a", fontSize: 12 }}>
                    Subtotal
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#344767", fontSize: 12 }}
                  >
                    ₹{subTotal?.toLocaleString("en-IN")}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: "#4CAF50", fontSize: 12 }}>
                    Discount
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#4CAF50", fontSize: 12 }}
                  >
                    −₹{appliedDiscount?.toLocaleString("en-IN")}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* ── Grand Total ── */}
          <Box
            sx={{
              borderRadius: 2.5,
              p: 2.5,
              backgroundColor: grandTotal > 0 ? "#A68181" : "#f0f2f5",
              transition: "background-color 0.3s",
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: grandTotal > 0 ? "#ffffff" : "#A68181" }}
                >
                  Total Amount
                </Typography>
              </Box>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ color: grandTotal > 0 ? "#ffffff" : "#7b809a" }}
              >
                {grandTotal > 0 ? `₹${grandTotal?.toLocaleString("en-IN")}` : "₹0"}
              </Typography>
            </Box>
          </Box>

          {activeStep === 1 && (
            <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
              <Button
                type="submit"
                disabled={loading || !canStep1}
                size="large"
                sx={{
                  px: 5,
                  borderRadius: 8,
                  textTransform: "none",
                  fontSize: 14,
                  boxShadow: "none",
                  backgroundColor: "#A68181",
                  color: "#f3f1f1",
                  "&:hover": { backgroundColor: "#9A3B3B", color: "#ffff", boxShadow: "none" },
                  "&:disabled": { backgroundColor: "#dee2e6", color: "#6c757d" },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  <>
                    Confirm Booking
                    <Iconify sx={{ ml: 1 }} icon="mdi:calendar-check-outline" />
                  </>
                )}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Grid>
  );
};
